import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * Privacy Policy Screen
 * - Public legal page for SafeVibe.dev
 * - Follows reference design: no card, no shadow, just centered content
 * - Uses Tailwind spacing, color, and font conventions
 * - No session or navigation logic
 * - Logs mount for debug/dev
 */
const PrivacyPolicyScreen: React.FC = () => {
  useEffect(() => {
    // Minimal debug log for compliance
    console.log('Privacy Policy screen loaded');
  }, []);

  return (
    <>
      <Navbar />
      <div className="max-w-2xl mx-auto py-12 px-4 text-gray-800" data-testid="privacy-policy-content">
        {/* Page Title */}
        <h1 className="text-3xl font-bold mb-4">Privacy Policy – SafeVibe.dev</h1>
        {/* Meta Info */}
        <p className="mb-2 text-sm text-gray-500">Effective Date: 26.6.2025</p>
        <p className="mb-2 text-sm text-gray-500">Operator: SafeVibe.dev</p>
        <p className="mb-2 text-sm text-gray-500">Address: Beethovenstrasse 48, 8002 Zurich</p>
        <p className="mb-6 text-sm text-gray-500">Contact Email: hello@safevibe.dev</p>

        {/* Section 1: Overview */}
        <h2 className="text-xl font-semibold mt-8 mb-2">1. Overview</h2>
        <p className="mb-4">SafeVibe.dev is committed to protecting your privacy and handling your data with care and transparency. This Privacy Policy explains what data we collect, why we collect it, and how we use it — in compliance with Swiss law and the GDPR (where applicable).</p>

        {/* Section 2: What Data We Collect */}
        <h2 className="text-xl font-semibold mt-8 mb-2">2. What Data We Collect</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Email address when you sign up or request fixes</li>
          <li>Uploaded files or GitHub URLs for code scanning (temporarily stored, auto-deleted after 7 days)</li>
          <li>Basic analytics (privacy-friendly, no tracking cookies)</li>
          <li>Payment data is processed via Stripe (we never store card details)</li>
        </ul>
        <p className="mb-4">We do not collect sensitive personal data unless you include it in uploaded files (which are deleted automatically).</p>

        {/* Section 3: How We Use Your Data */}
        <h2 className="text-xl font-semibold mt-8 mb-2">3. How We Use Your Data</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Provide scanning feedback and fix suggestions</li>
          <li>Contact you with onboarding or follow-up info</li>
          <li>Improve the performance and reliability of our service</li>
          <li>Fulfill legal or tax obligations (e.g., accounting for payments)</li>
        </ul>
        <p className="mb-4">We do not sell, rent, or share your data with third parties except as required to provide our service (see below).</p>

        {/* Section 4: Data Security */}
        <h2 className="text-xl font-semibold mt-8 mb-2">4. Data Security</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>All access is protected by authentication and encryption (HTTPS)</li>
          <li>Uploaded files are auto-deleted after 7 days</li>
          <li>Supabase storage buckets are private by default</li>
          <li>API keys and secrets are never exposed to the frontend</li>
        </ul>

        {/* Section 5: Data Retention */}
        <h2 className="text-xl font-semibold mt-8 mb-2">5. Data Retention</h2>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-left border">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-3">Data Type</th>
                <th className="py-2 px-3">Retention Policy</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="py-1 px-3">Email addresses</td><td className="py-1 px-3">Until you request deletion</td></tr>
              <tr><td className="py-1 px-3">Uploaded code</td><td className="py-1 px-3">Auto-deleted after 7 days</td></tr>
              <tr><td className="py-1 px-3">Scan results</td><td className="py-1 px-3">Stored per account, deleted on request</td></tr>
              <tr><td className="py-1 px-3">Payment receipts</td><td className="py-1 px-3">Stored for 10 years (Swiss legal requirement)</td></tr>
            </tbody>
          </table>
        </div>

        {/* Section 6: Third-Party Services */}
        <h2 className="text-xl font-semibold mt-8 mb-2">6. Third-Party Services</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Supabase – Database, file storage (EU-hosted)</li>
          <li>Stripe – Payment processing (no card details stored on our servers)</li>
          <li>OpenAI – Used for fix suggestions (data is not stored or reused)</li>
          <li>Email provider – Transactional emails only</li>
        </ul>
        <p className="mb-4">These services may store data outside of Switzerland or the EU, but only in countries with adequate data protection laws.</p>

        {/* Section 7: Your Rights */}
        <h2 className="text-xl font-semibold mt-8 mb-2">7. Your Rights</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Access, correct, or delete your data</li>
          <li>Withdraw consent at any time</li>
          <li>File a complaint with a Swiss or EU data protection authority</li>
        </ul>
        <p className="mb-4">To exercise these rights, email us at hello@safevibe.dev.</p>

        {/* Section 8: Legal Basis */}
        <h2 className="text-xl font-semibold mt-8 mb-2">8. Legal Basis</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Swiss data protection law</li>
          <li>GDPR (where applicable)</li>
        </ul>

        {/* Section 9: Limitation of Liability */}
        <h2 className="text-xl font-semibold mt-8 mb-2">9. Limitation of Liability</h2>
        <p className="mb-4">SafeVibe.dev is provided "as is" and without any warranty. We do not guarantee that our service is error-free, secure, or will meet your needs. To the maximum extent permitted by law, we disclaim all liability for any direct, indirect, incidental, or consequential damages arising from your use of this website or our services.</p>

        {/* Section 10: Changes to This Policy */}
        <h2 className="text-xl font-semibold mt-8 mb-2">10. Changes to This Policy</h2>
        <p className="mb-4">We may update this Privacy Policy from time to time. Changes will be posted on this page with a new effective date. Continued use of SafeVibe.dev constitutes acceptance of the updated policy.</p>

        {/* Section 11: Contact */}
        <h2 className="text-xl font-semibold mt-8 mb-2">11. Contact</h2>
        <p className="mb-2">Email: hello@safevibe.dev</p>
        <p className="mb-2">Address: Beethovenstrasse 48, 8002 Zurich</p>

        {/* Back to Home link for navigation */}
        <div className="flex justify-end mt-8">
          <Link to="/" className="text-blue-600 font-semibold hover:underline">Back to Home</Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicyScreen; 